import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  InputBase,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Typography,
  Divider,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Groups as GroupsIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  globalSearch,
  GlobalSearchResults,
  totalResultCount,
} from '../../services/globalSearchService';

const memberName = (m: any) =>
  m.displayName || `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email || 'Member';

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GlobalSearchResults | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await globalSearch(q, 5);
      setResults(r);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const seeAll = () => {
    if (query.trim().length < 2) return;
    go(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      seeAll();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const count = results ? totalResultCount(results) : 0;
  const sections = results
    ? [
        {
          key: 'members',
          label: 'Members',
          icon: <PersonIcon fontSize="small" />,
          items: results.members.map(m => ({
            id: m.id,
            title: memberName(m),
            subtitle: (m as any).chapter || (m as any).email || '',
            avatar: (m as any).photoUrl,
            badge: null,
            path: `/directory?member=${m.id}`,
          })),
        },
        {
          key: 'events',
          label: 'Events',
          icon: <EventIcon fontSize="small" />,
          items: results.events.map(e => ({
            id: e.id,
            title: e.title,
            subtitle: e.subtitle,
            avatar: e.coverImageUrl,
            badge: null,
            path: `/events/${e.id}`,
          })),
        },
        {
          key: 'groups',
          label: 'Groups',
          icon: <GroupsIcon fontSize="small" />,
          items: results.groups.map(g => ({
            id: g.id,
            title: g.title,
            subtitle: g.subtitle,
            avatar: null,
            badge: null,
            path: g.slug ? `/groups/${g.slug}` : '/groups',
          })),
        },
        // A published issue has a public page; a draft or scheduled one does not
        // yet, so send the user to the editor rather than a 404.
        {
          key: 'newsletters',
          label: 'Newsletters',
          icon: <ArticleIcon fontSize="small" />,
          items: results.newsletters.map(n => ({
            id: n.id,
            title: n.title,
            subtitle: n.subtitle,
            avatar: n.coverImageUrl,
            badge: n.status && n.status !== 'published' ? n.status : null,
            path:
              n.status === 'published' && n.slug
                ? `/newsletters/${n.slug}`
                : `/admin/newsletters/${n.id}/edit`,
          })),
        },
        {
          key: 'surveys',
          label: 'Surveys',
          icon: <AssignmentIcon fontSize="small" />,
          items: results.surveys.map(s => ({
            id: s.id,
            title: s.title,
            subtitle: s.subtitle,
            avatar: null,
            badge: null,
            path: s.slug ? `/surveys/${s.slug}` : '/surveys',
          })),
        },
      ].filter(s => s.items.length > 0)
    : [];

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: 'relative', flexGrow: 1, maxWidth: 560, mx: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.25,
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
          <InputBase
            fullWidth
            placeholder="Search members, events, groups and more..."
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: 14 }}
            inputProps={{ 'aria-label': 'global search' }}
          />
          {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Paper>

        <Popper
          open={open && query.trim().length >= 2}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: anchorRef.current?.clientWidth }}
        >
          <Paper elevation={6} sx={{ mt: 0.5, maxHeight: 460, overflow: 'auto', borderRadius: 2 }}>
            {loading && count === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : count === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No results for &ldquo;{query.trim()}&rdquo;
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {sections.map(section => (
                  <li key={section.key}>
                    <ul style={{ padding: 0 }}>
                      <ListSubheader
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          lineHeight: '32px',
                          bgcolor: 'background.paper',
                        }}
                      >
                        {section.icon}
                        {section.label}
                      </ListSubheader>
                      {section.items.map(item => (
                        <ListItemButton
                          key={`${section.key}-${item.id}`}
                          onClick={() => go(item.path)}
                        >
                          <ListItemAvatar sx={{ minWidth: 44 }}>
                            <Avatar src={item.avatar || undefined} sx={{ width: 32, height: 32 }}>
                              {section.icon}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              item.badge ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {item.title}
                                  </Box>
                                  <Chip
                                    label={item.badge}
                                    size="small"
                                    sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }}
                                  />
                                </Box>
                              ) : (
                                item.title
                              )
                            }
                            secondary={item.subtitle}
                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                            secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                          />
                        </ListItemButton>
                      ))}
                    </ul>
                  </li>
                ))}
                <Divider />
                <ListItemButton onClick={seeAll} sx={{ justifyContent: 'center' }}>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    See all results for &ldquo;{query.trim()}&rdquo;
                  </Typography>
                </ListItemButton>
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
